import { AppPlanInfo, getPlanName, PlanPrivacy, PlanType, SubscriptionType } from "common/app-plans";
import { IconImage } from "common/components/SerializedApp";
import { styled } from "sharedUI/styles";
import AppPlanPreview from "./app-plan-preview";
import { PaymentInfoController } from "designer/modals/smart/payment-info-modal/payment-info-controller";
import { useCallback, useMemo } from "react";
import AppPlanBillingInfo from "./app-plan-billing-info";
import { CardPreview } from "common/firebase-function-types";
import { GlideFC } from "lib/glide-fc";
import { Owner } from "common/Database";
import { CardInfo } from "designer/modals/smart/payment-info-modal/payment-info";
import { CardLoading } from "./app-plan-loading";
import { CardState, getCardState } from "designer/builder/ui/smart/unified-app-plan/app-settings-plan-lib";

export enum BillingView {
    Loading,
    Summary,
    CardEntry,
}

const PlanBillingContainer = styled.div`
    display: flex;
    align-items: flex-start;
    width: 100%;

    .card-info {
        display: flex;
        width: 450px;
        margin: 0px 8px 0px 40px;
    }
`;

interface Props {
    billingView: BillingView;
    fromPanel: boolean;
    owner: Owner;
    appPlanInfo: AppPlanInfo;
    plan: PlanType;
    privacy: PlanPrivacy;
    subscription: SubscriptionType;
    numPrivateUsers: number | undefined;
    appAccentColor: string;
    appIcon: IconImage;
    estimate: number | undefined;
    cardPreview: CardPreview | undefined;
    paymentSetupIntent: string | undefined;
    cardError: boolean;
    updateBillingView: (view: BillingView) => void;
    privateUsersChange: (numUsers: number) => void;
    subscriptionChange: (subscription: SubscriptionType) => void;
    updatePaymentSetupIntent: (setupIntent: string) => void;
    updatePaymentInfo: (cardInfo: CardInfo, stripeCard: any) => void;
    updateCanAcceptNewCard: (canAccept: boolean) => void;
    onPromoCodeApplied: () => void;
    onPayButton: (paymentMethodId: string) => Promise<boolean>;
}

const AppPlanBilling: GlideFC<Props> = p => {
    const {
        billingView,
        updateBillingView,
        fromPanel,
        appPlanInfo,
        plan,
        privacy,
        subscription,
        numPrivateUsers,
        appAccentColor,
        appIcon,
        privateUsersChange,
        subscriptionChange,
        estimate,
        cardPreview,
        owner,
        updatePaymentInfo,
        updateCanAcceptNewCard,
        cardError,
        onPromoCodeApplied,
        onPayButton,
    } = p;

    const onEditBilling = useCallback(() => {
        updateBillingView(BillingView.CardEntry);
    }, [updateBillingView]);

    const onCancelCardEntry = useCallback(() => {
        updateBillingView(BillingView.Summary);
    }, [updateBillingView]);

    const cardState = getCardState(cardPreview);

    const paymentRequestInfo = useMemo(() => {
        return {
            estimateDollars: estimate ?? 0,
            label: getPlanName(appPlanInfo.code).long,
        };
    }, [appPlanInfo.code, estimate]);

    let content = <CardLoading />;
    if (billingView === BillingView.CardEntry) {
        content = (
            <div className="card-info">
                <PaymentInfoController
                    padTop={false}
                    onCanAcceptChange={updateCanAcceptNewCard}
                    onUpdateCard={updatePaymentInfo}
                    inlineCancel={true}
                    onInlineCancel={onCancelCardEntry}
                    cardError={cardError}
                    onPayButton={onPayButton}
                    paymentRequestInfo={paymentRequestInfo}
                />
            </div>
        );
    } else if (
        billingView === BillingView.Summary &&
        cardPreview !== undefined &&
        cardState === CardState.CardPresent
    ) {
        content = (
            <div className="card-info">
                <AppPlanBillingInfo cardPreview={cardPreview} editBilling={onEditBilling} />
            </div>
        );
    }

    return (
        <PlanBillingContainer>
            <AppPlanPreview
                fromPanel={fromPanel}
                appPlanInfo={appPlanInfo}
                locked={false}
                plan={plan}
                privacy={privacy}
                subscription={subscription}
                numPrivateUsers={numPrivateUsers}
                appAccentColor={appAccentColor}
                appIcon={appIcon}
                privateUsersChange={privateUsersChange}
                subscriptionChange={subscriptionChange}
                estimate={estimate}
                onEditPrivateUsers={undefined}
                owner={owner}
                onPromoCodeApplied={onPromoCodeApplied}
            />
            {content}
        </PlanBillingContainer>
    );
};

export default AppPlanBilling;
